import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuthState } from '@/auth/AuthProvider';

type UiMode = 'detailed' | 'simple';

interface UiModeContextValue {
  uiMode: UiMode;
  setUiMode: (mode: UiMode) => void;
  isLoading: boolean;
}

const UiModeContext = createContext<UiModeContextValue | undefined>(undefined);

export function useUiMode() {
  const context = useContext(UiModeContext);
  if (!context) {
    throw new Error('useUiMode must be used within a UiModeProvider');
  }
  return context;
}

interface UiModeProviderProps {
  children: ReactNode;
}

export function UiModeProvider({ children }: UiModeProviderProps) {
  const { user } = useAuthState();
  const [uiMode, setUiModeState] = useState<UiMode>('detailed');
  const [isLoading, setIsLoading] = useState(true);

  // Load initial UI mode from Firestore or localStorage
  useEffect(() => {
    async function loadUiMode() {
      try {
        if (user?.uid) {
          // Try to load from Firestore
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const savedMode = userSnap.data()?.uiMode as UiMode | undefined;
            if (savedMode === 'detailed' || savedMode === 'simple') {
              setUiModeState(savedMode);
            }
          }
        } else {
          // Fallback to localStorage for non-authenticated users
          const savedMode = localStorage.getItem('threadlock-ui-mode') as UiMode | null;
          if (savedMode === 'detailed' || savedMode === 'simple') {
            setUiModeState(savedMode);
          }
        }
      } catch (error) {
        console.error('Failed to load UI mode:', error);
        // Fallback to localStorage
        const savedMode = localStorage.getItem('threadlock-ui-mode') as UiMode | null;
        if (savedMode === 'detailed' || savedMode === 'simple') {
          setUiModeState(savedMode);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadUiMode();
  }, [user?.uid]);

  // Persist UI mode changes
  const setUiMode = async (mode: UiMode) => {
    setUiModeState(mode);
    
    try {
      if (user?.uid) {
        // Save to Firestore
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { uiMode: mode }, { merge: true });
      }
      
      // Always save to localStorage as backup
      localStorage.setItem('threadlock-ui-mode', mode);
    } catch (error) {
      console.error('Failed to persist UI mode:', error);
      // Still save to localStorage even if Firestore fails
      localStorage.setItem('threadlock-ui-mode', mode);
    }
  };

  const value: UiModeContextValue = {
    uiMode,
    setUiMode,
    isLoading,
  };

  return (
    <UiModeContext.Provider value={value}>
      {children}
    </UiModeContext.Provider>
  );
}
